import StatusBadge from '../StatusBadge';
import { SignedAmountInput } from '../SignedAmountInput';
import { Button } from '../ui/button';
import { Label } from '../ui/label';
import { Input } from '../ui/input';
import { Checkbox } from '../ui/checkbox';
import { Dialog, DialogContent, DialogHeader, DialogTitle } from '../ui/dialog';
import { Calendar } from '../ui/calendar';
import { Popover, PopoverContent, PopoverTrigger } from '../ui/popover';
import { Calendar as CalendarIcon, CheckCircle2 } from 'lucide-react';
import { format } from 'date-fns';
import { id } from 'date-fns/locale';
import api from '../../lib/api';
import { toast } from 'sonner';
import { formatRupiah } from '../../utils/printHelper';


export function POSubmitToAPDialog({
  open, onOpenChange, selectedPO, invoiceNumber, setInvoiceNumber,
  tanggalInvoice, setTanggalInvoice, tanggalTerima, setTanggalTerima,
  partialItems, handlePartialItemChange, handleSubmitToAP,
  submitUsesVA, setSubmitUsesVA, submitVAData, setSubmitVAData,
  submitPembulatan, setSubmitPembulatan, submitBiayaAngkut, setSubmitBiayaAngkut
}) {
  const handleVAToggle = async (checked) => {
    setSubmitUsesVA(checked);
    if (checked && selectedPO?.supplier_id && selectedPO?.company_id) {
      try {
        const vaRes = await api.get(`/virtual-accounts/lookup?supplier_id=${selectedPO.supplier_id}&company_id=${selectedPO.company_id}`);
        setSubmitVAData(vaRes.data);
      } catch {
        setSubmitVAData(null);
        toast.error('Virtual Account tidak ditemukan untuk kombinasi supplier & company ini');
      }
    } else {
      setSubmitVAData(null);
    }
  };

  const handleCancel = () => {
    onOpenChange(false);
    setInvoiceNumber('');
    setSubmitUsesVA(false);
    setSubmitVAData(null);
  };

  return (
    <Dialog open={open} onOpenChange={onOpenChange}>
      <DialogContent className="max-w-3xl max-h-[90vh] overflow-y-auto">
        <DialogHeader>
          <DialogTitle>Submit ke Account Payable - Buat Invoice</DialogTitle>
        </DialogHeader>
        {selectedPO && (
          <div className="space-y-4">
            <div className="bg-indigo-50 p-3 rounded-lg">
              <p className="text-sm"><strong>No. PO:</strong> {selectedPO.po_number}</p>
              <p className="text-sm"><strong>Supplier:</strong> {selectedPO.supplier_name}</p>
              <p className="text-sm"><strong>Status PO:</strong> <StatusBadge status={selectedPO.status} /></p>
              {(selectedPO.biaya_angkut > 0 || selectedPO.has_dp) && (
                <div className="mt-2 pt-2 border-t border-indigo-200 space-y-1">
                  {selectedPO.biaya_angkut > 0 && (
                    <div className="text-sm text-indigo-700">
                      <strong>Biaya Angkut (dari PO):</strong> Rp {(selectedPO.biaya_angkut || 0).toLocaleString('id-ID')}
                      {selectedPO.biaya_angkut_terinvoice > 0 && (
                        <span className="ml-2 text-indigo-500">
                          (Telah di-invoice: Rp {(selectedPO.biaya_angkut_terinvoice || 0).toLocaleString('id-ID')} | Sisa alokasi: Rp {(selectedPO.biaya_angkut_sisa || 0).toLocaleString('id-ID')})
                        </span>
                      )}
                    </div>
                  )}
                  {selectedPO.has_dp && (
                    <p className="text-sm text-amber-700"><strong>Down Payment:</strong> {selectedPO.dp_number} - Rp {(selectedPO.dp_amount || 0).toLocaleString('id-ID')}</p>
                  )}
                </div>
              )}
            </div>

            <div className="grid grid-cols-2 gap-4">
              <div className="space-y-2">
                <Label>Nomor Invoice *</Label>
                <Input value={invoiceNumber} onChange={(e) => setInvoiceNumber(e.target.value.toUpperCase())} placeholder="MASUKKAN NOMOR INVOICE" style={{ textTransform: 'uppercase' }} className="font-mono" data-testid="invoice-number-input" />
              </div>
              <div className="space-y-2">
                <Label>Tanggal Invoice *</Label>
                <Popover modal={false}>
                  <PopoverTrigger asChild>
                    <Button variant="outline" className="w-full justify-start text-left font-normal">
                      <CalendarIcon className="mr-2 h-4 w-4" />
                      {tanggalInvoice ? format(tanggalInvoice, 'dd MMMM yyyy', { locale: id }) : 'Pilih tanggal invoice'}
                    </Button>
                  </PopoverTrigger>
                  <PopoverContent className="w-auto p-0 z-[100]" align="start">
                    <Calendar mode="single" selected={tanggalInvoice} onSelect={setTanggalInvoice} initialFocus />
                  </PopoverContent>
                </Popover>
              </div>
            </div>

            <div className="space-y-2">
              <Label>Tanggal Terima Barang *</Label>
              <Popover modal={false}>
                <PopoverTrigger asChild>
                  <Button variant="outline" className="w-full justify-start text-left font-normal">
                    <CalendarIcon className="mr-2 h-4 w-4" />
                    {tanggalTerima ? format(tanggalTerima, 'dd MMMM yyyy', { locale: id }) : 'Pilih tanggal terima'}
                  </Button>
                </PopoverTrigger>
                <PopoverContent className="w-auto p-0 z-[100]" align="start">
                  <Calendar mode="single" selected={tanggalTerima} onSelect={setTanggalTerima} initialFocus />
                </PopoverContent>
              </Popover>
            </div>

            {/* Virtual Account Checkbox */}
            <div className="border rounded-lg p-3 bg-amber-50 border-amber-200">
              <div className="flex items-center gap-3">
                <Checkbox id="submit-uses-va" checked={submitUsesVA} onCheckedChange={handleVAToggle} data-testid="submit-uses-va" />
                <Label htmlFor="submit-uses-va" className="cursor-pointer text-amber-800 font-semibold">Menggunakan Virtual Account</Label>
              </div>
              {submitUsesVA && submitVAData && (
                <div className="mt-2 ml-7 p-2 bg-white rounded border border-amber-300 text-sm space-y-1">
                  <p><span className="text-slate-500">Nomor VA:</span> <span className="font-mono font-bold text-amber-700">{submitVAData.nomor_va}</span></p>
                  <p><span className="text-slate-500">Nama VA:</span> <span className="font-bold text-amber-700">{submitVAData.nama_va}</span></p>
                </div>
              )}
              {submitUsesVA && !submitVAData && (
                <p className="mt-2 ml-7 text-xs text-red-600">VA belum terdaftar untuk supplier & company ini. Hubungi Purchasing untuk menambahkan data VA.</p>
              )}
            </div>

            {/* Pembulatan & Biaya Angkut - editable by gudang */}
            <div className="grid grid-cols-2 gap-4">
              <div className="space-y-2">
                <Label>Pembulatan Harga (Rp)</Label>
                <SignedAmountInput
                  value={submitPembulatan}
                  onChange={setSubmitPembulatan}
                  data-testid="submit-pembulatan-input"
                />
                <p className="text-xs text-slate-500">Koreksi pada total invoice, tidak mempengaruhi DPP maupun PPn. Boleh diisi minus.</p>
              </div>
              <div className="space-y-2">
                <Label>Biaya Angkut (Rp) {selectedPO?.biaya_angkut_sisa !== undefined ? `(Sisa Alokasi: Rp ${(selectedPO.biaya_angkut_sisa || 0).toLocaleString('id-ID')})` : ''}</Label>
                <Input
                  type="number"
                  value={submitBiayaAngkut}
                  onChange={(e) => setSubmitBiayaAngkut(parseFloat(e.target.value) || 0)}
                  min="0"
                  placeholder="0"
                  data-testid="submit-biaya-angkut-input"
                />
                <p className="text-xs text-slate-500">Biaya angkut per pengiriman ini</p>
              </div>
            </div>

            <div className="space-y-3">
              <Label>Item yang akan disubmit ke AP:</Label>
              {(() => {
                // Show rows that can be submitted, plus rows the warehouse has
                // not received yet — the latter greyed out with a badge, so it
                // is obvious why they cannot be picked. Fully-invoiced rows
                // stay hidden entirely.
                const visibleItems = partialItems.filter(
                  (it) => it.belum_diterima
                    || (it.received && !it.fully_invoiced && ((it.remaining_qty ?? it.quantity) > 0))
                );

                if (visibleItems.length === 0) {
                  return (
                    <div
                      className="border rounded-lg p-6 text-center text-slate-600 bg-green-50"
                      data-testid="all-invoiced-empty-state"
                    >
                      <CheckCircle2 className="h-8 w-8 text-green-600 mx-auto mb-2" />
                      <p className="font-medium">Semua item sudah selesai di-invoice.</p>
                    </div>
                  );
                }

                return (
                  <div className="border rounded-lg overflow-hidden">
                    <div className="grid grid-cols-12 gap-2 px-3 py-2 bg-slate-50 text-xs font-semibold text-slate-600 uppercase tracking-wide">
                      <div className="col-span-4">Nama Item</div>
                      <div className="col-span-1 text-right">Qty PO</div>
                      <div className="col-span-2 text-right">Qty Diterima</div>
                      <div className="col-span-2 text-right">Sisa u/ Invoice</div>
                      <div className="col-span-1 text-right">Harga</div>
                      <div className="col-span-2 text-right">Subtotal</div>
                    </div>
                    <div className="divide-y">
                      {visibleItems.map((item) => {
                        const origIndex = partialItems.findIndex((pi) => pi.line_id === item.line_id);
                        const qty = item.received_qty ?? item.quantity ?? 0;
                        const subtotal = qty * (item.unit_price || 0);
                        return (
                          <div
                            key={item.line_id || `${item.name}-${item.delivery_date}-${origIndex}`}
                            className={`grid grid-cols-12 gap-2 px-3 py-2.5 items-center text-sm ${item.belum_diterima ? 'bg-slate-50 opacity-60' : 'bg-white'}`}
                            data-testid={`partial-item-${item.line_id || origIndex}`}
                          >
                            <div className="col-span-4">
                              <p className="font-semibold text-slate-800">
                                {item.name}
                                {item.belum_diterima && (
                                  <span className="ml-2 text-[10px] bg-amber-100 text-amber-700 px-1.5 py-0.5 rounded font-semibold align-middle" data-testid={`belum-diterima-${item.line_id || origIndex}`}>
                                    Belum diterima
                                  </span>
                                )}
                              </p>
                              {item.delivery_date && (
                                <p className="text-xs text-slate-500 mt-0.5">
                                  Kirim: {item.delivery_date}
                                </p>
                              )}
                            </div>
                            <div className="col-span-1 text-right font-mono text-slate-600">
                              {item.original_qty ?? item.quantity} {item.satuan}
                            </div>
                            <div className="col-span-2 text-right font-mono text-slate-700">
                              {item.received_qty ?? 0} {item.satuan}
                            </div>
                            <div className="col-span-2 text-right font-mono font-semibold text-indigo-700">
                              {item.remaining_qty ?? qty} {item.satuan}
                            </div>
                            <div className="col-span-1 text-right font-mono text-slate-700">
                              Rp {formatRupiah(item.unit_price || 0)}
                            </div>
                            <div className="col-span-2 text-right font-mono font-semibold text-slate-800">
                              Rp {formatRupiah(subtotal)}
                            </div>
                          </div>
                        );
                      })}
                    </div>
                  </div>
                );
              })()}
            </div>

            <div className="bg-indigo-50 border border-indigo-200 p-3 rounded-lg space-y-1">
              {(() => {
                const itemsSubtotal = partialItems
                  .filter((item) => item.received && !item.fully_invoiced && ((item.remaining_qty ?? item.quantity) > 0))
                  .reduce((sum, item) => sum + ((item.received_qty ?? item.quantity) * (item.unit_price || 0)), 0);
                const pembulatan = submitPembulatan || 0;
                const biayaAngkut = submitBiayaAngkut || 0;
                const hasPpn = selectedPO?.has_ppn || false;
                const ppnPct = selectedPO?.ppn_percentage || 0;
                // Pembulatan corrects the total, not the tax base - see utils/invoiceAmounts.js
                const ppnAmount = hasPpn ? itemsSubtotal * (ppnPct / 100) : 0;
                const total = itemsSubtotal + ppnAmount + biayaAngkut + pembulatan;
                return (
                  <>
                    <div className="flex justify-between text-sm">
                      <span>Subtotal Item</span>
                      <span className="font-mono">Rp {formatRupiah(itemsSubtotal)}</span>
                    </div>
                    {hasPpn && (
                      <div className="flex justify-between text-sm">
                        <span>PPn ({ppnPct}%)</span>
                        <span className="font-mono">Rp {formatRupiah(ppnAmount)}</span>
                      </div>
                    )}
                    {biayaAngkut > 0 && (
                      <div className="flex justify-between text-sm">
                        <span>Biaya Angkut</span>
                        <span className="font-mono">Rp {biayaAngkut.toLocaleString('id-ID')}</span>
                      </div>
                    )}
                    {pembulatan !== 0 && (
                      <div className="flex justify-between text-sm">
                        <span>Pembulatan</span>
                        <span className="font-mono">{pembulatan >= 0 ? '+' : ''}Rp {pembulatan.toLocaleString('id-ID')}</span>
                      </div>
                    )}
                    <div className="flex justify-between font-bold border-t pt-1">
                      <span>Total Invoice</span>
                      <span className="font-mono text-indigo-600">Rp {formatRupiah(total)}</span>
                    </div>
                  </>
                );
              })()}
            </div>

            <div className="flex gap-2 justify-end">
              <Button variant="outline" onClick={handleCancel}>Batal</Button>
              <Button
                className="bg-indigo-600 hover:bg-indigo-700"
                onClick={handleSubmitToAP}
                disabled={
                  !partialItems.some((item) => item.received && !item.fully_invoiced && ((item.remaining_qty ?? item.quantity) > 0))
                  || !invoiceNumber.trim()
                }
              >
                Buat Invoice & Submit ke AP
              </Button>
            </div>
          </div>
        )}
      </DialogContent>
    </Dialog>
  );
}



